import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, TextInput, Modal, ActivityIndicator, SafeAreaView, Image } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Alert } from '../../utils/toast';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import BusinessCard from '../dashboard/BusinessCard';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { getClubById, acceptJoinRequest, rejectJoinRequest, removeMember, makeCaptain, removeCaptain, Club, ClubMember as APIClubMember, ClubJoinRequest as APIClubJoinRequest, getPendingExitRequests, processExitRequest, ClubExitRequest } from '../../lib/api/clubs';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { storage } from '../../utils/storage';

interface ClubMember {
  id: string;
  userId: string;
  name: string;
  profession?: string;
  city?: string;
  avatar?: string;
  joinedAt: string;
  role?: 'member' | 'captain';
}

interface JoinRequest {
  id: string;
  userId: string;
  userName: string;
  userProfession?: string;
  userPhoto?: string;
  phone: string;
  formData?: any;
  createdAt: string;
  status?: 'pending' | 'approved'; // Add status field
}

interface ClubMembersTabProps {
  clubId: string;
  isAdmin: boolean;
}

const ClubMembersTab: React.FC<ClubMembersTabProps> = ({ clubId, isAdmin }) => {
  const { theme } = useTheme();
  const navigation = useNavigation<any>();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const safeBottomInset = Math.max(bottomInset, 16);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [pendingRequests, setPendingRequests] = useState<JoinRequest[]>([]);
  const [exitRequests, setExitRequests] = useState<ClubExitRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<JoinRequest | null>(null);
  const [selectedExitRequest, setSelectedExitRequest] = useState<ClubExitRequest | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showExitRequestModal, setShowExitRequestModal] = useState(false);
  const [showMemberMenu, setShowMemberMenu] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ClubMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [isViewingMemberForm, setIsViewingMemberForm] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    loadMembersAndRequests();
    loadCurrentUser();
  }, [clubId]);

  const loadCurrentUser = async () => {
    try {
      const user = await storage.getUser();
      console.log('👤 Fetched user:', user);
      if (user?.userId) {
        setCurrentUserId(user.userId);
        console.log('✅ Set current user ID:', user.userId);
      } else {
        console.log('❌ No user or user.userId found');
      }
    } catch (error) {
      console.error('❌ Error fetching user:', error);
    }
  };

  const loadMembersAndRequests = async () => {
    try {
      setLoading(true);
      const club = await getClubById(clubId);
      
      // Transform members
      const transformedMembers = club.members?.map(m => ({
        id: m.id,
        userId: m.userId,
        name: m.user.name,
        profession: m.user.profession,
        avatar: m.user.photo,
        joinedAt: m.joinedAt,
        role: m.role || 'member', // Include role field
        city: (m.user as any).city,
        businessName: (m.user as any).businessName,
        businessIndustry: (m.user as any).businessIndustry,
        businessPhoto: (m.user as any).businessPhoto,
        phone: (m as any).phone,
        formData: (m as any).formData,
      })) || [];
      
      console.log('📥 Loaded members:', {
        totalMembers: transformedMembers.length,
        membersWithFormData: transformedMembers.filter(m => (m as any).formData).length,
        allMembersWithRoles: transformedMembers.map(m => ({
          name: m.name,
          userId: m.userId,
          role: m.role
        })),
        sampleMember: transformedMembers[0] ? {
          id: transformedMembers[0].id,
          name: transformedMembers[0].name,
          hasPhone: !!(transformedMembers[0] as any).phone,
          hasFormData: !!(transformedMembers[0] as any).formData,
          formDataKeys: (transformedMembers[0] as any).formData ? Object.keys((transformedMembers[0] as any).formData) : [],
        } : null,
      });
      
      setMembers(transformedMembers);

      // Check if current user is a captain
      const user = await storage.getUser();
      const userId = user?.userId;
      const currentUserMember = transformedMembers.find(m => m.userId === userId);
      const userIsCaptain = currentUserMember?.role === 'captain';

      // Transform join requests (for admins or captains)
      if ((isAdmin || userIsCaptain) && club.joinRequests) {
        const transformedRequests = club.joinRequests
          .filter(r => r.status === 'pending' || r.status === 'approved') // Include both pending and approved
          .map(r => ({
            id: r.id,
            userId: r.userId,
            userName: r.user.name,
            userProfession: r.user.profession,
            userPhoto: r.user.photo,
            phone: r.phone,
            formData: (r as any).formData,
            createdAt: r.createdAt,
            status: r.status as 'pending' | 'approved', // Include status
          }));
        setPendingRequests(transformedRequests);
      }

      // Load exit requests (for admins or captains)
      if (isAdmin || userIsCaptain) {
        const exitReqs = await getPendingExitRequests(clubId);
        setExitRequests(exitReqs);
      }
    } catch (error) {
      console.error('Error loading members:', error);
      Alert.alert('Error', 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const closeRequestModal = () => {
    setShowRequestModal(false);
    setIsViewingMemberForm(false);
    setSelectedRequest(null);
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      await acceptJoinRequest(clubId, requestId);
      showSuccessToast('Member request accepted');
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      closeRequestModal();
      loadMembersAndRequests(); // Reload to update members list
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await rejectJoinRequest(clubId, requestId);
      Alert.alert('Success', 'Member request rejected');
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      closeRequestModal();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to reject request');
    }
  };

  const handleApproveExitRequest = async (requestId: string) => {
    try {
      await processExitRequest(clubId, requestId, 'approve');
      showSuccessToast('Exit request approved. Member removed from club.');
      setExitRequests(prev => prev.filter(r => r.id !== requestId));
      setShowExitRequestModal(false);
      loadMembersAndRequests(); // Reload to update members list
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to approve exit request');
    }
  };

  const handleRejectExitRequest = async (requestId: string) => {
    try {
      await processExitRequest(clubId, requestId, 'reject');
      showSuccessToast('Exit request rejected');
      setExitRequests(prev => prev.filter(r => r.id !== requestId));
      setShowExitRequestModal(false);
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to reject exit request');
    }
  };

  const handleRemoveMember = async (userId: string, memberName: string) => {
    Alert.alert(
      'Remove Member',
      `Are you sure you want to remove ${memberName} from this club?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              console.log('🗑️ Removing member:', { clubId, userId, memberName });
              await removeMember(clubId, userId);
              setMembers(prev => prev.filter(m => m.userId !== userId));
              Alert.alert('Success', `${memberName} has been removed`);
            } catch (error: any) {
              console.error('❌ Error removing member:', error);
              Alert.alert('Error', error.message || 'Failed to remove member');
            }
          },
        },
      ]
    );
  };

  const handleMakeCaptain = async (userId: string, memberName: string) => {
    setShowMemberMenu(false);
    Alert.alert(
      'Make Captain',
      `Are you sure you want to make ${memberName} a captain? They will be able to approve/reject members and edit posting days.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Make Captain',
          onPress: async () => {
            try {
              console.log('👑 Making member captain:', { clubId, userId, memberName });
              await makeCaptain(clubId, userId);
              // Update local state
              setMembers(prev => prev.map(m => 
                m.userId === userId ? { ...m, role: 'captain' as const } : m
              ));
              showSuccessToast(`${memberName} is now a captain`);
            } catch (error: any) {
              console.error('❌ Error making captain:', error);
              showErrorToast(error.message || 'Failed to make captain');
            }
          },
        },
      ]
    );
  };

  const handleRemoveCaptain = async (userId: string, memberName: string) => {
    setShowMemberMenu(false);
    Alert.alert(
      'Remove Captain',
      `Are you sure you want to remove captain role from ${memberName}? They will become a regular member.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove Captain',
          style: 'destructive',
          onPress: async () => {
            try {
              console.log('👤 Removing captain role:', { clubId, userId, memberName });
              await removeCaptain(clubId, userId);
              // Update local state
              setMembers(prev => prev.map(m => 
                m.userId === userId ? { ...m, role: 'member' as const } : m
              ));
              showSuccessToast(`${memberName} is now a regular member`);
            } catch (error: any) {
              console.error('❌ Error removing captain:', error);
              showErrorToast(error.message || 'Failed to remove captain role');
            }
          },
        },
      ]
    );
  };

  const renderMember = (member: ClubMember) => {
    // Log member data for debugging
    if (member.id && Math.random() < 0.1) { // Log 10% of members to avoid spam
      console.log('🎴 Rendering member card:', {
        id: member.id,
        name: member.name,
        hasPhone: !!(member as any).phone,
        hasFormData: !!(member as any).formData,
      });
    }
    
    // Transform member data to match BusinessCard's expected userData format
    const userData = {
      id: member.userId,
      name: member.name,
      designation: member.profession,
      city: member.city,
      photo: member.avatar,
      businessName: (member as any).businessName,
      businessIndustry: (member as any).businessIndustry,
      businessPhoto: (member as any).businessPhoto,
    };

    // Check if current user is a captain by finding them in members array
    const currentUserMember = members.find(m => m.userId === currentUserId);
    const isCaptain = currentUserMember?.role === 'captain';
    
    // Show 3-dot menu for admins or captains
    const canManageMembers = isAdmin || isCaptain;
    
    console.log('🔍 Render check:', { 
      currentUserId, 
      isAdmin, 
      isCaptain, 
      canManageMembers,
      currentUserRole: currentUserMember?.role 
    });
    
    const topRightContent = canManageMembers ? (
      <TouchableOpacity
        style={[styles.adminMenuButton, { backgroundColor: theme.card, borderColor: theme.border }]}
        onPress={(e) => {
          e.stopPropagation();
          setSelectedMember(member);
          setShowMemberMenu(true);
        }}
        activeOpacity={0.7}
      >
        <Ionicons name="ellipsis-horizontal" size={20} color={theme.text} />
      </TouchableOpacity>
    ) : null;

    // Add captain badge as custom content
    const captainBadge = member.role === 'captain' ? (
      <View style={[styles.captainBadge, { backgroundColor: theme.backgroundSecondary, borderColor: theme.accent }]}>
        <Ionicons name="ribbon" size={12} color={theme.accent} />
        <ThemedText style={[styles.captainText, { color: theme.accent }]}>Captain</ThemedText>
      </View>
    ) : null;

    return (
      <View key={member.id} style={styles.memberCardWrapper}>
        <BusinessCard userData={userData} topRightContent={topRightContent} />
        {captainBadge && (
          <View style={styles.captainBadgeContainer}>
            {captainBadge}
          </View>
        )}
      </View>
    );
  };

  const renderJoinRequest = (request: JoinRequest) => {
    const isApproved = request.status === 'approved';
    
    return (
      <View
        key={request.id}
        style={[styles.requestCard, { backgroundColor: theme.card, borderColor: theme.border }]}
      >
        <View style={styles.requestHeader}>
          <TouchableOpacity
            style={[styles.avatar, { backgroundColor: theme.border }]}
            onPress={() => navigation.navigate('Profile', { userId: request.userId })}
            activeOpacity={0.7}
          >
            {request.userPhoto ? (
              <Image source={{ uri: request.userPhoto }} style={styles.avatarImage} />
            ) : (
              <Ionicons name="person" size={24} color={theme.textSecondary} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.requestInfo}
            onPress={() => navigation.navigate('Profile', { userId: request.userId })}
            activeOpacity={0.7}
          >
            <ThemedText style={[styles.requestName, { color: theme.text }]}>
              {request.userName}
            </ThemedText>
            {request.userProfession && (
              <ThemedText style={[styles.requestProfession, { color: theme.textSecondary }]}>
                {request.userProfession}
              </ThemedText>
            )}
            {isApproved && (
              <View style={[styles.statusBadge, { backgroundColor: '#10B981' + '20', borderColor: '#10B981' }]}>
                <Ionicons name="checkmark-circle" size={14} color="#10B981" />
                <ThemedText style={[styles.statusBadgeText, { color: '#10B981' }]}>
                  Approved - Awaiting Code
                </ThemedText>
              </View>
            )}
          </TouchableOpacity>
          <View style={styles.requestActions}>
            <TouchableOpacity
              style={[styles.iconButton, { backgroundColor: theme.accent + '20', borderColor: theme.accent, borderWidth: 1 }]}
              onPress={() => {
                setSelectedRequest(request);
                setIsViewingMemberForm(false);
                setShowRequestModal(true);
              }}
              activeOpacity={0.7}
            >
              <Ionicons name="document-text" size={18} color={theme.accent} />
            </TouchableOpacity>
            {!isApproved && (
              <>
                <TouchableOpacity
                  style={[styles.iconButton, { backgroundColor: '#10B981' }]}
                  onPress={() => handleAcceptRequest(request.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="checkmark" size={20} color="#FFFFFF" />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.iconButton, { backgroundColor: '#EF4444' }]}
                  onPress={() => handleRejectRequest(request.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="close" size={20} color="#FFFFFF" />
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
        {request.formData?.aboutBusiness && (
          <ThemedText
            style={[styles.requestPreview, { color: theme.textSecondary }]}
            numberOfLines={2}
          >
            {request.formData.aboutBusiness}
          </ThemedText>
        )}
      </View>
    );
  };

  const renderExitRequest = (request: ClubExitRequest) => (
    <View
      key={request.id}
      style={[styles.requestCard, { backgroundColor: theme.card, borderColor: theme.error + '40' }]}
    >
      <View style={styles.requestHeader}>
        <TouchableOpacity
          style={[styles.avatar, { backgroundColor: theme.border }]}
          onPress={() => navigation.navigate('Profile', { userId: request.userId })}
          activeOpacity={0.7}
        >
          {request.user.photo ? (
            <Image source={{ uri: request.user.photo }} style={styles.avatarImage} />
          ) : (
            <Ionicons name="person" size={24} color={theme.textSecondary} />
          )}
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.requestInfo}
          onPress={() => navigation.navigate('Profile', { userId: request.userId })}
          activeOpacity={0.7}
        >
          <ThemedText style={[styles.requestName, { color: theme.text }]}>
            {request.user.name}
          </ThemedText>
          {request.user.profession && (
            <ThemedText style={[styles.requestProfession, { color: theme.textSecondary }]}>
              {request.user.profession}
            </ThemedText>
          )}
        </TouchableOpacity>
        <View style={styles.requestActions}>
          {request.note && (
            <TouchableOpacity
              style={[styles.iconButton, { backgroundColor: theme.error + '20', borderColor: theme.error, borderWidth: 1 }]}
              onPress={() => {
                setSelectedExitRequest(request);
                setShowExitRequestModal(true);
              }}
              activeOpacity={0.7}
            >
              <Ionicons name="document-text" size={18} color={theme.error} />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.iconButton, { backgroundColor: '#10B981' }]}
            onPress={() => handleApproveExitRequest(request.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="checkmark" size={20} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.iconButton, { backgroundColor: '#EF4444' }]}
            onPress={() => handleRejectExitRequest(request.id)}
            activeOpacity={0.7}
          >
            <Ionicons name="close" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>
      {request.note && (
        <ThemedText
          style={[styles.requestPreview, { color: theme.textSecondary }]}
          numberOfLines={2}
        >
          Note: {request.note}
        </ThemedText>
      )}
    </View>
  );

  // Check if current user is a captain
  const currentUserMember = members.find(m => m.userId === currentUserId);
  const isCaptain = currentUserMember?.role === 'captain';

  return (
    <View style={styles.container}>
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Pending Requests Section (Admin or Captain) */}
        {(isAdmin || isCaptain) && pendingRequests.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
                Pending Join Requests
              </ThemedText>
              <View style={[styles.badge, { backgroundColor: theme.accent }]}>
                <ThemedText style={styles.badgeText}>
                  {pendingRequests.length}
                </ThemedText>
              </View>
            </View>
            {pendingRequests.map(renderJoinRequest)}
          </View>
        )}

        {/* Pending Exit Requests Section (Admin or Captain) */}
        {(isAdmin || isCaptain) && exitRequests.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
                Pending Exit Requests
              </ThemedText>
              <View style={[styles.badge, { backgroundColor: theme.error }]}>
                <ThemedText style={styles.badgeText}>
                  {exitRequests.length}
                </ThemedText>
              </View>
            </View>
            {exitRequests.map(renderExitRequest)}
          </View>
        )}

        {/* Members Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
              Members ({members.length})
            </ThemedText>
          </View>

          {members.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="people-outline" size={48} color={theme.textTertiary} />
              <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
                No members yet
              </ThemedText>
            </View>
          ) : (
            members.map(renderMember)
          )}
        </View>
      </ScrollView>
      )}

      {/* Join Request Detail Modal */}
      <Modal
        visible={showRequestModal}
        transparent
        animationType="slide"
        onRequestClose={closeRequestModal}
      >
        <SafeAreaView style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={[styles.modalContent, { backgroundColor: theme.card, paddingBottom: safeBottomInset + 16 }]}>
            <View style={styles.modalHeader}>
              <TouchableOpacity
                onPress={closeRequestModal}
                activeOpacity={0.7}
                style={styles.backButton}
              >
                <Ionicons name="arrow-back" size={24} color={theme.text} />
              </TouchableOpacity>
              <ThemedText style={[styles.modalTitle, { color: theme.text }]}>
                Join Request
              </ThemedText>
              <View style={{ width: 24 }} />
            </View>

            {selectedRequest && (
              <>
                <ScrollView
                  style={styles.modalBody}
                  contentContainerStyle={{ paddingBottom: safeBottomInset + 16 }}
                >
                  <TouchableOpacity
                    style={styles.requesterInfo}
                    onPress={() => {
                      closeRequestModal();
                      navigation.navigate('Profile', { userId: selectedRequest.userId });
                    }}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.avatar, { backgroundColor: theme.border }]}>
                      {selectedRequest.userPhoto ? (
                        <Image source={{ uri: selectedRequest.userPhoto }} style={styles.avatarImage} />
                      ) : (
                        <Ionicons name="person" size={32} color={theme.textSecondary} />
                      )}
                    </View>
                    <View style={styles.requesterDetails}>
                      <ThemedText style={[styles.requesterName, { color: theme.text }]}>
                        {selectedRequest.userName}
                      </ThemedText>
                      {selectedRequest.userProfession && (
                        <ThemedText style={[styles.requesterProfession, { color: theme.textSecondary }]}>
                          {selectedRequest.userProfession}
                        </ThemedText>
                      )}
                    </View>
                  </TouchableOpacity>

                  {/* Phone */}
                  <View style={styles.formField}>
                    <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                      Phone
                    </ThemedText>
                    <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                      {selectedRequest.phone}
                    </ThemedText>
                  </View>

                  {/* Form Data Fields */}
                  {selectedRequest.formData && (
                    <>
                      {selectedRequest.formData.aboutBusiness && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            About Business
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.aboutBusiness}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.industry && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Industry
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.industry}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.referredBy && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Referred By
                          </ThemedText>
                          {selectedRequest.formData.referredById ? (
                            <TouchableOpacity
                              onPress={() => {
                                console.log('🔗 Navigating to referrer profile:', {
                                  referredBy: selectedRequest.formData.referredBy,
                                  referredById: selectedRequest.formData.referredById,
                                });
                                closeRequestModal();
                                navigation.navigate('Profile', { userId: selectedRequest.formData.referredById });
                              }}
                              activeOpacity={0.7}
                              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                            >
                              <ThemedText style={[styles.fieldValue, styles.clickableField, { color: theme.accent }]}>
                                {selectedRequest.formData.referredBy}
                              </ThemedText>
                            </TouchableOpacity>
                          ) : (
                            <>
                              <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                                {selectedRequest.formData.referredBy}
                              </ThemedText>
                              <ThemedText style={[styles.fieldLabel, { color: theme.textTertiary, fontSize: 11, marginTop: 4 }]}>
                                (No profile link available)
                              </ThemedText>
                            </>
                          )}
                        </View>
                      )}

                      {selectedRequest.formData.socialNetworkId && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Social Network ID
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.socialNetworkId}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.contactSize && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Contact Size
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.contactSize}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.contactSphere && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Contact Sphere
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.contactSphere}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.salesTurnover && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Sales Turnover
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.salesTurnover}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.businessPlace && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Business Place
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.businessPlace}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.cityResidentFrom && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            City Resident From
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.cityResidentFrom}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.whyJoining && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Why Joining
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.whyJoining}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.link && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Link
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.link}
                          </ThemedText>
                        </View>
                      )}

                      {selectedRequest.formData.idProof && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            ID Proof
                          </ThemedText>
                          <Image
                            source={{ uri: selectedRequest.formData.idProof }}
                            style={styles.idProofImage}
                            resizeMode="contain"
                          />
                        </View>
                      )}

                      {selectedRequest.formData.requestMessage && (
                        <View style={styles.formField}>
                          <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                            Additional Message
                          </ThemedText>
                          <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                            {selectedRequest.formData.requestMessage}
                          </ThemedText>
                        </View>
                      )}
                    </>
                  )}
                </ScrollView>

                {!isViewingMemberForm && (
                  <View style={[styles.modalActions, { paddingBottom: safeBottomInset }]}>
                    <TouchableOpacity
                      style={[styles.modalButton, { backgroundColor: '#EF4444' }]}
                      onPress={() => handleRejectRequest(selectedRequest.id)}
                      activeOpacity={0.8}
                    >
                      <Ionicons name="close-circle" size={20} color="#FFFFFF" />
                      <ThemedText style={styles.modalButtonText}>Reject</ThemedText>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.modalButton, { backgroundColor: '#10B981' }]}
                      onPress={() => handleAcceptRequest(selectedRequest.id)}
                      activeOpacity={0.8}
                    >
                      <Ionicons name="checkmark-circle" size={20} color="#FFFFFF" />
                      <ThemedText style={styles.modalButtonText}>Accept</ThemedText>
                    </TouchableOpacity>
                  </View>
                )}
              </>
            )}
            </View>
          </View>
        </SafeAreaView>
      </Modal>

      {/* Exit Request Detail Modal */}
      <Modal
        visible={showExitRequestModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowExitRequestModal(false)}
      >
        <SafeAreaView style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={[styles.modalContent, { backgroundColor: theme.card, paddingBottom: safeBottomInset + 16 }]}>
              <View style={styles.modalHeader}>
                <TouchableOpacity
                  onPress={() => setShowExitRequestModal(false)}
                  activeOpacity={0.7}
                  style={styles.backButton}
                >
                  <Ionicons name="arrow-back" size={24} color={theme.text} />
                </TouchableOpacity>
                <ThemedText style={[styles.modalTitle, { color: theme.text }]}>
                  Exit Request
                </ThemedText>
                <View style={{ width: 24 }} />
              </View>

              {selectedExitRequest && (
                <>
                  <ScrollView
                    style={styles.modalBody}
                    contentContainerStyle={{ paddingBottom: safeBottomInset + 16 }}
                  >
                    <TouchableOpacity
                      style={styles.requesterInfo}
                      onPress={() => {
                        setShowExitRequestModal(false);
                        navigation.navigate('Profile', { userId: selectedExitRequest.userId });
                      }}
                      activeOpacity={0.7}
                    >
                      <View style={[styles.avatar, { backgroundColor: theme.border }]}>
                        {selectedExitRequest.user.photo ? (
                          <Image source={{ uri: selectedExitRequest.user.photo }} style={styles.avatarImage} />
                        ) : (
                          <Ionicons name="person" size={32} color={theme.textSecondary} />
                        )}
                      </View>
                      <View style={styles.requesterDetails}>
                        <ThemedText style={[styles.requesterName, { color: theme.text }]}>
                          {selectedExitRequest.user.name}
                        </ThemedText>
                        {selectedExitRequest.user.profession && (
                          <ThemedText style={[styles.requesterProfession, { color: theme.textSecondary }]}>
                            {selectedExitRequest.user.profession}
                          </ThemedText>
                        )}
                      </View>
                    </TouchableOpacity>

                    {/* Exit Note */}
                    {selectedExitRequest.note && (
                      <View style={styles.formField}>
                        <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                          Reason for Leaving
                        </ThemedText>
                        <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                          {selectedExitRequest.note}
                        </ThemedText>
                      </View>
                    )}

                    {/* Phone */}
                    {selectedExitRequest.user.phone && (
                      <View style={styles.formField}>
                        <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                          Phone
                        </ThemedText>
                        <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                          {selectedExitRequest.user.phone}
                        </ThemedText>
                      </View>
                    )}

                    {/* Email */}
                    {selectedExitRequest.user.email && (
                      <View style={styles.formField}>
                        <ThemedText style={[styles.fieldLabel, { color: theme.textSecondary }]}>
                          Email
                        </ThemedText>
                        <ThemedText style={[styles.fieldValue, { color: theme.text }]}>
                          {selectedExitRequest.user.email}
                        </ThemedText>
                      </View>
                    )}
                  </ScrollView>

                  <View style={[styles.modalActions, { paddingBottom: safeBottomInset }]}>
                    <TouchableOpacity
                      style={[styles.modalButton, { backgroundColor: '#EF4444' }]}
                      onPress={() => handleRejectExitRequest(selectedExitRequest.id)}
                      activeOpacity={0.8}
                    >
                      <Ionicons name="close-circle" size={20} color="#FFFFFF" />
                      <ThemedText style={styles.modalButtonText}>Reject</ThemedText>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.modalButton, { backgroundColor: '#10B981' }]}
                      onPress={() => handleApproveExitRequest(selectedExitRequest.id)}
                      activeOpacity={0.8}
                    >
                      <Ionicons name="checkmark-circle" size={20} color="#FFFFFF" />
                      <ThemedText style={styles.modalButtonText}>Approve & Remove</ThemedText>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>
          </View>
        </SafeAreaView>
      </Modal>

      {/* Member Action Menu Modal */}
      <Modal
        visible={showMemberMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMemberMenu(false)}
      >
        <TouchableOpacity 
          style={styles.menuOverlay}
          activeOpacity={1}
          onPress={() => setShowMemberMenu(false)}
        >
          <View
            style={[
              styles.menuContent,
              { backgroundColor: theme.card, paddingBottom: 20 + bottomInset }
            ]}
          >
            <ThemedText style={[styles.menuTitle, { color: theme.text }]}>
              {selectedMember?.name}
            </ThemedText>
            
            <TouchableOpacity
              style={[styles.menuOption, { borderBottomColor: theme.border }]}
              onPress={() => {
                console.log('👤 View Join Form clicked for member:', {
                  memberId: selectedMember?.id,
                  memberName: selectedMember?.name,
                  hasPhone: !!(selectedMember as any)?.phone,
                  hasFormData: !!(selectedMember as any)?.formData,
                  phone: (selectedMember as any)?.phone,
                  formDataKeys: (selectedMember as any)?.formData ? Object.keys((selectedMember as any).formData) : [],
                });
                
                setShowMemberMenu(false);
                if (selectedMember && (selectedMember as any).formData) {
                  // Create a JoinRequest object from member data
                  const memberAsRequest: JoinRequest = {
                    id: selectedMember.id,
                    userId: selectedMember.userId,
                    userName: selectedMember.name,
                    userProfession: selectedMember.profession,
                    userPhoto: selectedMember.avatar,
                    phone: (selectedMember as any).phone || '',
                    formData: (selectedMember as any).formData,
                    createdAt: selectedMember.joinedAt,
                  };
                  console.log('✅ Opening join form modal with data:', memberAsRequest);
                  setSelectedRequest(memberAsRequest);
                  setIsViewingMemberForm(true);
                  setShowRequestModal(true);
                } else {
                  console.log('❌ No form data available for member');
                  Alert.alert('No Form Data', 'Join request form data is not available for this member.');
                }
              }}
              activeOpacity={0.7}
            >
              <Ionicons name="document-text-outline" size={22} color={theme.accent} />
              <ThemedText style={[styles.menuOptionText, { color: theme.text }]}>
                View Join Form
              </ThemedText>
            </TouchableOpacity>

            {/* Remove Member - Admin or Captain */}
            {(isAdmin || isCaptain) && (
              <TouchableOpacity
                style={styles.menuOption}
                onPress={() => {
                  setShowMemberMenu(false);
                  if (selectedMember) {
                    handleRemoveMember(selectedMember.userId, selectedMember.name);
                  }
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="trash-outline" size={22} color="#EF4444" />
                <ThemedText style={[styles.menuOptionText, { color: '#EF4444' }]}>
                  Remove Member
                </ThemedText>
              </TouchableOpacity>
            )}

            {/* Make/Remove Captain - Admin only */}
            {isAdmin && (
              <TouchableOpacity
                style={styles.menuOption}
                onPress={() => {
                  if (selectedMember) {
                    if (selectedMember.role === 'captain') {
                      handleRemoveCaptain(selectedMember.userId, selectedMember.name);
                    } else {
                      handleMakeCaptain(selectedMember.userId, selectedMember.name);
                    }
                  }
                }}
                activeOpacity={0.7}
              >
                <Ionicons 
                  name={selectedMember?.role === 'captain' ? "person-outline" : "ribbon-outline"} 
                  size={22} 
                  color={theme.accent} 
                />
                <ThemedText style={[styles.menuOptionText, { color: theme.text }]}>
                  {selectedMember?.role === 'captain' ? 'Make Member' : 'Make Captain'}
                </ThemedText>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.menuCancelButton, { backgroundColor: theme.background }]}
              onPress={() => setShowMemberMenu(false)}
              activeOpacity={0.7}
            >
              <ThemedText style={[styles.menuCancelText, { color: theme.text }]}>
                Cancel
              </ThemedText>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  badge: {
    marginLeft: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '700',
  },
  memberCardWrapper: {
    marginBottom: 12,
  },
  captainBadgeContainer: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 1,
  },
  captainBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    gap: 4,
  },
  captainText: {
    fontSize: 12,
    fontWeight: '600',
  },
  adminMenuButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  requestCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    marginBottom: 8,
  },
  requestHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  requestInfo: {
    flex: 1,
    marginLeft: 12,
  },
  requestActions: {
    flexDirection: 'row',
    gap: 8,
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  requestName: {
    fontSize: 16,
    fontWeight: '600',
  },
  requestProfession: {
    fontSize: 14,
    marginTop: 2,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 6,
    alignSelf: 'flex-start',
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  requestPreview: {
    fontSize: 14,
    lineHeight: 20,
  },
  exitNotePreview: {
    fontSize: 13,
    marginTop: 4,
    fontStyle: 'italic',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 18,
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalContent: {
    maxHeight: '90%',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  modalBody: {
    padding: 20,
  },
  requesterInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  requesterDetails: {
    marginLeft: 12,
  },
  requesterName: {
    fontSize: 18,
    fontWeight: '700',
  },
  requesterProfession: {
    fontSize: 14,
    marginTop: 2,
  },
  messageContainer: {
    marginTop: 12,
  },
  messageLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  messageBox: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 22,
  },
  formField: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  fieldValue: {
    fontSize: 15,
    lineHeight: 22,
  },
  clickableField: {
    textDecorationLine: 'underline',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 16,
  },
  modalButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
  },
  modalButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  idProofImage: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    marginTop: 8,
  },
  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  menuContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
  },
  menuTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 20,
    textAlign: 'center',
  },
  menuOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 1,
    gap: 12,
  },
  menuOptionText: {
    fontSize: 16,
    fontWeight: '500',
  },
  menuCancelButton: {
    marginTop: 12,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  menuCancelText: {
    fontSize: 16,
    fontWeight: '600',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ClubMembersTab;
